import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Mail, Settings, Server, Key, Send, CheckCircle, 
  AlertCircle, Loader2, Eye, EyeOff, TestTube
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SuperAdminEmailSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  
  const [config, setConfig] = useState({
    is_enabled: true,
    service_type: 'resend', // resend, smtp
    sender_email: 'noreply@cloudcampus.com',
    sender_name: 'Cloud Campus',
    // Resend
    resend_api_key: '',
    // SMTP
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_use_tls: true,
    // Notification settings
    send_payment_confirmation: true,
    send_payment_approval: true,
    send_payment_rejection: true,
    send_expiry_reminder: true,
    expiry_reminder_days: [7, 3, 1] // Days before expiry to send reminder
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/superadmin/email-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.settings) {
          setConfig(prev => ({ ...prev, ...data.settings }));
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/superadmin/email-settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });
      
      if (response.ok) {
        toast.success('Email settings saved successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save settings');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }
    
    setTesting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/superadmin/email-settings/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ test_email: testEmail })
      });
      
      if (response.ok) {
        toast.success('Test email sent successfully! Check your inbox.');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send test email');
      }
    } catch (error) {
      toast.error('Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="superadmin-email-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Settings</h1>
          <p className="text-gray-500">Configure email service for sending subscription notifications to tenants</p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
          Save Settings
        </Button>
      </div>

      {/* Enable/Disable */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Mail className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium">Email Service</p>
                <p className="text-sm text-gray-500">Enable or disable all email notifications</p>
              </div>
            </div>
            <Switch
              checked={config.is_enabled}
              onCheckedChange={(checked) => setConfig({ ...config, is_enabled: checked })}
            />
          </div>
        </CardContent>
      </Card>

      {config.is_enabled && (
        <>
          {/* Service Type */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Server className="h-5 w-5" />
                Email Service Configuration
              </CardTitle>
              <CardDescription>Choose your email service provider</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Service Type</Label>
                  <Select
                    value={config.service_type}
                    onValueChange={(value) => setConfig({ ...config, service_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="resend">Resend API</SelectItem>
                      <SelectItem value="smtp">SMTP (Gmail, Outlook, Custom)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Sender Email</Label>
                  <Input
                    value={config.sender_email}
                    onChange={(e) => setConfig({ ...config, sender_email: e.target.value })}
                    placeholder="noreply@cloudcampus.com"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Sender Name</Label>
                  <Input
                    value={config.sender_name}
                    onChange={(e) => setConfig({ ...config, sender_name: e.target.value })}
                    placeholder="Cloud Campus"
                  />
                </div>
              </div>

              {/* Resend Config */}
              {config.service_type === 'resend' && (
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200 space-y-4">
                  <div className="flex items-center gap-2 text-purple-700">
                    <Key className="h-4 w-4" />
                    <span className="font-medium">Resend Configuration</span>
                  </div>
                  <div className="space-y-2">
                    <Label>Resend API Key</Label>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={config.resend_api_key}
                        onChange={(e) => setConfig({ ...config, resend_api_key: e.target.value })}
                        placeholder="re_xxxxxxxxxxxx"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <p className="text-sm text-gray-500">
                      Get your API key from <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">resend.com</a>
                    </p>
                  </div>
                </div>
              )}

              {/* SMTP Config */}
              {config.service_type === 'smtp' && (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200 space-y-4">
                  <div className="flex items-center gap-2 text-green-700">
                    <Server className="h-4 w-4" />
                    <span className="font-medium">SMTP Configuration</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>SMTP Host</Label>
                      <Input
                        value={config.smtp_host}
                        onChange={(e) => setConfig({ ...config, smtp_host: e.target.value })}
                        placeholder="smtp.gmail.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>SMTP Port</Label>
                      <Input
                        type="number"
                        value={config.smtp_port}
                        onChange={(e) => setConfig({ ...config, smtp_port: parseInt(e.target.value) })}
                        placeholder="587"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Username</Label>
                      <Input
                        value={config.smtp_username}
                        onChange={(e) => setConfig({ ...config, smtp_username: e.target.value })}
                        placeholder="your@gmail.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Password / App Password</Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={config.smtp_password}
                          onChange={(e) => setConfig({ ...config, smtp_password: e.target.value })}
                          placeholder="••••••••"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={config.smtp_use_tls}
                      onCheckedChange={(checked) => setConfig({ ...config, smtp_use_tls: checked })}
                    />
                    <Label>Use TLS/SSL</Label>
                  </div>
                  
                  {/* SMTP Presets */}
                  <div className="pt-2 border-t">
                    <p className="text-sm text-gray-600 mb-2">Quick presets:</p>
                    <div className="flex flex-wrap gap-2">
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => setConfig({
                          ...config,
                          smtp_host: 'smtp.gmail.com',
                          smtp_port: 587,
                          smtp_use_tls: true
                        })}
                      >
                        Gmail
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => setConfig({
                          ...config,
                          smtp_host: 'smtp.office365.com',
                          smtp_port: 587,
                          smtp_use_tls: true
                        })}
                      >
                        Outlook/Office365
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => setConfig({
                          ...config,
                          smtp_host: 'smtp.zoho.com',
                          smtp_port: 587,
                          smtp_use_tls: true
                        })}
                      >
                        Zoho
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notification Types */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Notification Settings
              </CardTitle>
              <CardDescription>Choose which emails to send automatically</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-sm">Payment Confirmation</p>
                    <p className="text-xs text-gray-500">When online payment is successful</p>
                  </div>
                  <Switch
                    checked={config.send_payment_confirmation}
                    onCheckedChange={(checked) => setConfig({ ...config, send_payment_confirmation: checked })}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-sm">Payment Approval</p>
                    <p className="text-xs text-gray-500">When offline payment is approved</p>
                  </div>
                  <Switch
                    checked={config.send_payment_approval}
                    onCheckedChange={(checked) => setConfig({ ...config, send_payment_approval: checked })}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-sm">Payment Rejection</p>
                    <p className="text-xs text-gray-500">When offline payment is rejected</p>
                  </div>
                  <Switch
                    checked={config.send_payment_rejection}
                    onCheckedChange={(checked) => setConfig({ ...config, send_payment_rejection: checked })}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-sm">Expiry Reminders</p>
                    <p className="text-xs text-gray-500">Before subscription expires</p>
                  </div>
                  <Switch
                    checked={config.send_expiry_reminder}
                    onCheckedChange={(checked) => setConfig({ ...config, send_expiry_reminder: checked })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Test Email */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TestTube className="h-5 w-5" />
                Test Email
              </CardTitle>
              <CardDescription>Send a test email to verify your configuration</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="Enter email address for test"
                  className="flex-1"
                />
                <Button onClick={handleTestEmail} disabled={testing} variant="outline">
                  {testing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                  Send Test
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
